import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ModalShell from "../Templates/ModalShell";
import { Field, inputClass } from "../../components/forms/Field";
import MultiSelect from "../../components/forms/MultiSelect";
import { createMaterial, updateMaterial } from "../../services/materialsService";
import { formatApiError } from "../../utils/helpers/apiErrors";

const RESOURCE_TYPES = ["pdf", "video", "article", "link", "other"];

// Create + edit form for a material's CONTENT. `material` is null for create;
// `subjects` is the DomainSubject catalog. The write body matches the backend:
// title, description, resource_type, EITHER url OR file, plus subjects (uuids).
// Recommendation rules are edited per-school in AdaptationModal, not here.

// Mirrors the backend MATERIAL_FILE_EXTENSIONS allowlist (deliberately no
// executable/active types). MAX_FILE_MB is a client-side guard for a friendlier
// failure than a server reject; the backend should enforce its own limit too.
const FILE_ACCEPT =
  ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.mp4,.webm,.mov,.txt";
const MAX_FILE_MB = 50;

export default function MaterialFormModal({
  material,
  subjects,
  onClose,
  onSaved,
}) {
  const { t } = useTranslation();
  const isEdit = !!material;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: material?.title || "",
      description: material?.description || "",
      resource_type: material?.resource_type || "link",
      url: material?.url || "",
    },
  });

  // A material is either a link (url) or an upload (file). Default to whichever
  // the existing material already uses; default to link for new ones.
  const [sourceMode, setSourceMode] = useState(
    material?.file_url && !material?.url ? "file" : "link",
  );
  const [file, setFile] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState(
    () => (material?.subjects || []).map((s) => s.uuid),
  );
  const [isSaving, setIsSaving] = useState(false);

  const hasExistingFile = !!material?.file_url;

  const subjectOptions = useMemo(
    () => subjects.map((s) => ({ value: s.uuid, label: s.name })),
    [subjects],
  );

  const onSubmit = async (data) => {
    const url = (data.url || "").trim();

    // Enforce the backend's "file OR url" rule client-side. On edit, an existing
    // upload (file_url) counts even when no new file is chosen.
    if (sourceMode === "link") {
      if (!url) {
        toast.error(t("materials.sourceRequired"));
        return;
      }
      if (!/^https?:\/\/.+/i.test(url)) {
        toast.error(t("materials.invalidUrl"));
        return;
      }
    } else if (!file && !hasExistingFile) {
      toast.error(t("materials.fileRequired"));
      return;
    }

    const body = {
      title: data.title.trim(),
      description: (data.description || "").trim(),
      resource_type: data.resource_type,
      subjects: selectedSubjects,
    };
    if (sourceMode === "file") {
      if (file) body.file = file;
      // Keep any existing url empty when uploading a file as the source.
    } else {
      body.url = url;
    }

    setIsSaving(true);
    try {
      if (isEdit) {
        await updateMaterial({ uuid: material.uuid, body });
        toast.success(t("materials.updatedToast", { title: body.title }));
      } else {
        await createMaterial({ body });
        toast.success(t("materials.createdToast", { title: body.title }));
      }
      onSaved?.();
    } catch (err) {
      toast.error(
        formatApiError(
          err,
          isEdit ? t("materials.updateFailed") : t("materials.createFailed"),
        ),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} busy={isSaving} maxWidth="max-w-2xl">
      <h3 className="text-lg font-bold text-slate-900 pr-8 mb-4">
        {isEdit ? t("materials.editMaterial") : t("materials.addMaterial")}
      </h3>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col min-h-[65vh] max-h-[82vh]"
        noValidate
      >
        <div className="flex-1 space-y-4 overflow-y-auto pr-1">
        <Field label={t("materials.title")} error={errors.title?.message} required>
          <input
            type="text"
            disabled={isSaving}
            {...register("title", {
              required: t("materials.titleRequired"),
              maxLength: { value: 255, message: t("materials.titleMax") },
            })}
            className={inputClass(errors.title)}
            placeholder={t("materials.titlePlaceholder")}
          />
        </Field>

        <Field label={t("materials.description")}>
          <textarea
            rows={2}
            disabled={isSaving}
            {...register("description")}
            className={inputClass(errors.description)}
            placeholder={t("materials.descriptionPlaceholder")}
          />
        </Field>

        <Field label={t("materials.resourceType")} required>
          <select
            disabled={isSaving}
            {...register("resource_type")}
            className={inputClass(errors.resource_type)}
          >
            {RESOURCE_TYPES.map((rt) => (
              <option key={rt} value={rt}>
                {t(`materials.type.${rt}`)}
              </option>
            ))}
          </select>
        </Field>

        {/* Source: link OR file */}
        <Field label={t("materials.source")} required>
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 mb-2.5">
            <button
              type="button"
              disabled={isSaving}
              onClick={() => setSourceMode("link")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition ${
                sourceMode === "link"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {t("materials.sourceLink")}
            </button>
            <button
              type="button"
              disabled={isSaving}
              onClick={() => setSourceMode("file")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition ${
                sourceMode === "file"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {t("materials.sourceFile")}
            </button>
          </div>

          {sourceMode === "link" ? (
            <input
              type="url"
              disabled={isSaving}
              {...register("url")}
              className={inputClass(errors.url)}
              placeholder={t("materials.urlPlaceholder")}
            />
          ) : (
            <div>
              <input
                type="file"
                disabled={isSaving}
                accept={FILE_ACCEPT}
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  if (f && f.size > MAX_FILE_MB * 1024 * 1024) {
                    toast.error(t("materials.fileTooLarge", { mb: MAX_FILE_MB }));
                    e.target.value = "";
                    setFile(null);
                    return;
                  }
                  setFile(f);
                }}
                className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 file:cursor-pointer disabled:opacity-60"
              />
              {hasExistingFile && !file && (
                <p className="mt-1.5 text-xs text-slate-500">
                  {t("materials.currentFile")}{" "}
                  <a
                    href={material.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-indigo-600 hover:underline"
                  >
                    {t("materials.viewFile")}
                  </a>
                </p>
              )}
            </div>
          )}
        </Field>

        {/* Subjects multiselect */}
        <Field label={t("materials.subjects")}>
          {subjects.length === 0 ? (
            <p className="text-xs text-slate-500 italic">{t("materials.noSubjects")}</p>
          ) : (
            <MultiSelect
              options={subjectOptions}
              value={selectedSubjects}
              onChange={setSelectedSubjects}
              disabled={isSaving}
              placeholder={t("materials.subjectsPlaceholder")}
              emptyText={t("materials.subjectsNoMatch")}
            />
          )}
        </Field>

        </div>

        <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSaving
              ? t("common.saving")
              : isEdit
                ? t("materials.save")
                : t("materials.create")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
